import {
  Check,
  CheckCheck,
  ChevronDown,
  Clock,
  ListFilter,
  PencilLine,
  Search,
  Star,
  TriangleAlert,
  UserRound,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import type {
  AgentRef,
  ComposeDraftSummary,
  Conversation,
  ConversationDraftSummary,
  DispatchBooking,
  Inbox,
  SubjectBooking,
} from '../../api/types';
import { initialsOf, snippetOf, waitingFor } from '../../lib/commsFormat';
import { ContactAvatar } from '../../components/ContactAvatar';
import { channelMetaForInbox, INBOX_ICONS, inboxIconKey } from '../../lib/commsMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import { pressJob, useJobTargets } from './booking/jobTarget';
import { ReservationChip } from './booking/ReservationChip';
import { composeDraftRecipient, composeDraftSnippet } from './composeDrafts';
import { useComms } from './context';
import { useDraftSummaries } from './drafts';
import { useDismissOnOutside } from '../../lib/useDismiss';
import { ConversationRowsSkeleton } from './Skeletons';
import { ContactRoleChip } from './DispatchRoleChip';
import { contactDisplayName } from './dispatchIdentity';
import { useLatestBookings, useSubjectBookings } from './gmail/latestBookings';
import { useInfiniteScroll } from './useInfiniteScroll';

type ConversationSort = 'latest' | 'created_at' | 'waiting_since' | 'draft_updated' | 'last_outgoing';

export interface ListControls {
  q: string;
  setQ: (q: string) => void;
  unreadFirst: boolean;
  setUnreadFirst: (on: boolean) => void;
  sortBy: ConversationSort;
  setSortBy: (s: ConversationSort) => void;
  assigneeType: 'all' | 'me' | 'unassigned';
  setAssigneeType: (a: 'all' | 'me' | 'unassigned') => void;
  reload: () => void;
  /** Clear the unread badge across every channel currently in scope. */
  markAllSeen: () => void;
  markingSeen: boolean;
  /** What that will cover, in the words on screen — "Email", a mailbox name. */
  markAllScope: string;
  /** Label of the view the search is confined to, e.g. "All open". */
  scopeLabel: string;
  /** True once the search has been widened past that view. */
  searchAll: boolean;
  setSearchAll: (on: boolean) => void;
}

const SORT_LABELS: Record<ListControls['sortBy'], string> = {
  latest: 'Latest',
  created_at: 'Newest',
  waiting_since: 'Waiting longest',
  draft_updated: 'Draft edited',
  last_outgoing: 'Last replied',
};

/**
 * Orders that belong to one view and are offered only while standing in it.
 * `draft_updated` has nothing to sort on elsewhere — the server would quietly
 * fall back to Latest — and `last_replied` sinks every thread nobody has
 * answered, which is the wrong way up for a list an agent triages from.
 */
const VIEW_SORTS: ListControls['sortBy'][] = ['draft_updated', 'last_outgoing'];

const AGENT_LABELS: Record<ListControls['assigneeType'], string> = {
  all: 'Anyone',
  me: 'Me',
  unassigned: 'Unassigned',
};

function snippetFor(conversation: Conversation): string {
  const last = conversation.lastMessage;
  if (!last) return '';
  if (last.contentType === 'voice_call') {
    const call = last.contentAttributes.call;
    const direction = call?.direction === 'outbound' ? 'Outbound call' : 'Incoming call';
    return call?.result === 'Missed' || call?.status === 'no_answer'
      ? 'Missed call'
      : direction;
  }
  if (last.private) return `Note: ${snippetOf(last.content)}`;
  if (!last.content && last.attachments.length > 0) {
    return `Attachment · ${last.attachments[0].fileName ?? last.attachments[0].fileType}`;
  }
  return snippetOf(last.content);
}

function Row({
  conversation,
  inbox,
  agents,
  active,
  draft,
  reservation,
  named,
  onOpenReservation,
  onSelect,
}: {
  conversation: Conversation;
  inbox: Inbox | undefined;
  agents: AgentRef[];
  active: boolean;
  /** This agent's unsent reply here, live as they type it. */
  draft: ConversationDraftSummary | null;
  /**
   * The reservation this thread's subject names, or else the contact's last
   * one, when there is either.
   */
  reservation: DispatchBooking | undefined;
  /** What the subject carried, when that is where `reservation` came from. */
  named: SubjectBooking | null;
  /** Where a press on that reservation goes — null with nowhere to send it. */
  onOpenReservation: (() => void) | null;
  onSelect: () => void;
}) {
  const { listTime } = useTimeFormat();
  const contact = conversation.contact;
  // Dispatch's answer for a number-only contact — see dispatchIdentity.tsx.
  const name = contactDisplayName(contact);
  const meta = inbox ? channelMetaForInbox(inbox) : null;
  const ChannelIcon = inbox ? INBOX_ICONS[inboxIconKey(inbox)] : null;
  const subject =
    typeof conversation.additionalAttributes.mailSubject === 'string'
      ? conversation.additionalAttributes.mailSubject
      : (inbox?.name ?? '');
  const unread = conversation.unreadCount ?? 0;
  const waiting = conversation.status === 'open' ? waitingFor(conversation.waitingSince) : null;
  const assigneeName = conversation.assigneeIsBot
    ? null
    : (agents.find((a) => a.dashBoardId === conversation.assigneeDashBoardId)?.name ?? null);
  // The design's row shows compact assignee initials ("AK"), not the name.
  const assignee = conversation.assigneeIsBot ? 'Bot' : assigneeName && initialsOf(assigneeName);

  return (
    <button
      type="button"
      className={`cv-row${active ? ' active' : ''}${unread > 0 ? ' unread' : ''}`}
      onClick={(event) => {
        // The reservation chip is text inside this one button (a control in a
        // button is invalid markup), so a press on it is caught here: it opens
        // the booking, like the mail list's chip, and not the thread.
        if (onOpenReservation && (event.target as HTMLElement).closest('.cv-row-trip')) {
          onOpenReservation();
          return;
        }
        onSelect();
      }}
    >
      <div className="cv-row-bar" />
      <div className="cv-row-inner">
        <div className="cv-row-avatar-wrap">
          <ContactAvatar
            name={name}
            email={conversation.contact.email}
            size={36}
          />
          {meta && ChannelIcon && (
            <div className="cv-row-chdot" style={{ background: meta.muted }}>
              <ChannelIcon size={9} style={{ color: meta.color }} />
            </div>
          )}
        </div>
        <div className="cv-row-main">
          <div className="cv-row-top">
            {/* Read-only here on purpose: the row is a <button>, and a second
                one inside it is invalid markup that keyboard users land on
                twice. The toggle lives on the thread header; this is the row
                saying which threads carry it. */}
            {conversation.starred && (
              <Star
                size={11}
                className="cv-row-star"
                aria-label="Starred"
                style={{ color: 'var(--c-orange)', fill: 'var(--c-orange)' }}
              />
            )}
            <span className="cv-row-name">{name}</span>
            {/* The mail list's chip: Customer for a linked contact, else the
                role dispatch matched their number to. */}
            <ContactRoleChip contact={contact} />
            {conversation.status === 'snoozed' && (
              <span className="cv-row-role" style={{ background: 'var(--c-orange-muted)', color: 'var(--c-orange)' }}>
                Snoozed
              </span>
            )}
            {conversation.status === 'pending' && (
              <span className="cv-row-role" style={{ background: 'var(--c-primary-muted)', color: 'var(--c-primary)' }}>
                Bot
              </span>
            )}
            <span className="cv-row-time">{listTime(conversation.lastActivityAt)}</span>
          </div>
          <div className="cv-row-sub">{subject}</div>
          {/* The agent's own unsent reply takes the snippet line: on a list read
              as "what needs me", a message they have already started answering
              is better described by their half of it than by the customer's. */}
          {draft ? (
            <div className="cv-row-snippet draft">
              <span className="cv-row-draft">{draft.isNote ? 'Note draft' : 'Draft'}</span>
              {draft.preview ||
                (draft.attachmentCount > 0
                  ? `${draft.attachmentCount} attachment${draft.attachmentCount > 1 ? 's' : ''}`
                  : '')}
            </div>
          ) : (
            <div className="cv-row-snippet">{snippetFor(conversation)}</div>
          )}
          <div className="cv-row-meta">
            {/* First on the line, so it sits in one column down the list
                whatever labels a row carries — the thing a dispatcher scans
                for is the customer who has a car coming. */}
            {reservation && (
              <ReservationChip
                booking={reservation}
                base="cv-row-trip"
                inRow
                openable={onOpenReservation !== null}
                named={named}
              />
            )}
            {conversation.labels[0] && <span className="cv-row-tag">{conversation.labels[0]}</span>}
            {waiting && waiting !== 'now' && (
              <span className="cv-row-sla">
                <Clock size={9} />
                {waiting}
              </span>
            )}
            <span className="cv-row-assignee" title={assigneeName ?? undefined}>
              {assignee || '—'}
            </span>
            {unread > 0 && <span className="cv-row-unread">{unread}</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * A new message this agent started and has not sent. It has no conversation to
 * be a row of — that is created by the send — so the Drafts view draws it from
 * the draft itself: who it is for, which account it goes from, and what is in
 * the box.
 *
 * Deliberately shaped like a conversation row rather than set apart: an agent
 * reading this list is asking "what was I in the middle of", and a reply half
 * written on a thread and a message half written to somebody new are the same
 * answer. What marks it is the pencil and the word — clicking one reopens the
 * compose window rather than a thread, and the row has to say so before the
 * click, not after.
 */
function ComposeDraftRow({
  draft,
  inbox,
  onOpen,
}: {
  draft: ComposeDraftSummary;
  inbox: Inbox | undefined;
  onOpen: () => void;
}) {
  const { listTime } = useTimeFormat();
  const name = composeDraftRecipient(draft);
  const meta = inbox ? channelMetaForInbox(inbox) : null;
  const ChannelIcon = inbox ? INBOX_ICONS[inboxIconKey(inbox)] : null;

  return (
    <button type="button" className="cv-row cv-row-compose" onClick={onOpen}>
      <div className="cv-row-bar" />
      <div className="cv-row-inner">
        <div className="cv-row-avatar-wrap">
          {/* No `email`: the recipient may be an address typed a moment ago for
              somebody who is not a contact yet, and a Gravatar lookup would send
              a hash of it before the agent has decided to write to them. */}
          <ContactAvatar name={name} size={36} />
          {meta && ChannelIcon && (
            <div className="cv-row-chdot" style={{ background: meta.muted }}>
              <ChannelIcon size={9} style={{ color: meta.color }} />
            </div>
          )}
        </div>
        <div className="cv-row-main">
          <div className="cv-row-top">
            <span className="cv-row-name">{name}</span>
            <span className="cv-row-time">{listTime(draft.updatedAt)}</span>
          </div>
          <div className="cv-row-sub">{inbox?.name ?? 'New message'}</div>
          <div className="cv-row-snippet draft">
            <span className="cv-row-draft">
              <PencilLine size={9} />
              Unsent
            </span>
            {composeDraftSnippet(draft)}
          </div>
          <div className="cv-row-meta">
            {draft.attachmentCount > 0 && (
              <span className="cv-row-tag">
                {draft.attachmentCount} attachment{draft.attachmentCount > 1 ? 's' : ''}
              </span>
            )}
            <span className="cv-row-assignee">—</span>
          </div>
        </div>
      </div>
    </button>
  );
}

/** The middle pane: search + filter chips + conversation rows. */
export function ConversationList({
  rows,
  composeDrafts = [],
  onOpenComposeDraft,
  inboxById,
  agents,
  selectedId,
  onSelect,
  controls,
  loading,
  stale,
  refreshing,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  width,
  onResize,
  mail = false,
}: {
  rows: Conversation[];
  /**
   * The Drafts view only: unsent NEW messages, which have no conversation to be
   * rows of. Empty everywhere else — every other view is a queue of things that
   * exist, and an unsent message is not in any of them.
   */
  composeDrafts?: readonly ComposeDraftSummary[];
  onOpenComposeDraft?: (draft: ComposeDraftSummary) => void;
  inboxById: Map<number, Inbox>;
  agents: AgentRef[];
  selectedId: number | null;
  onSelect: (conversation: Conversation) => void;
  controls: ListControls;
  loading: boolean;
  /** The rows belong to the mailbox/view you just switched away from. */
  stale: boolean;
  /** The rows are for this filter, but a fresher copy is on its way. */
  refreshing: boolean;
  /** The list request failed — shown instead of an empty state, which would lie. */
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  width: number;
  onResize: (width: number) => void;
  /**
   * The Email tab: rows read like a mail client's, an unread one raised and
   * bold over read ones sunk into a tint. Only there — a list of calls and
   * texts is not read the way a mailbox is, and the badge already says it.
   */
  mail?: boolean;
}) {
  // Live rather than read off the row: the composer updates this store as the
  // agent types, so the "Draft" line appears while they are still writing it
  // and disappears the moment they send.
  const drafts = useDraftSummaries();
  const { source } = useComms();
  // Each customer's last reservation, for the chip on their row — one batched
  // read per page, kept a minute, shared with the mail layout's list (see
  // gmail/latestBookings). Any channel: an SMS from a linked customer is as
  // much theirs as a mail is.
  const lastReservations = useLatestBookings(source, rows);
  // A mail row whose subject names a reservation shows THAT one instead —
  // "Re: Booking Confirmation # 11410" is about 11410, whatever was booked since.
  const subjectReservations = useSubjectBookings(source, rows);
  const jobTargets = useJobTargets();
  const [openMenu, setOpenMenu] = useState<'sort' | 'agent' | 'markseen' | null>(null);
  // Sort, agent and the mark-all confirm share one state, so only one panel is
  // ever mounted to hold this.
  const menuRef = useDismissOnOutside<HTMLDivElement>(openMenu !== null, () => setOpenMenu(null));
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: width };
    const move = (ev: MouseEvent) => {
      const drag = dragState.current;
      if (!drag) return;
      onResize(Math.max(280, Math.min(560, drag.startWidth + ev.clientX - drag.startX)));
    };
    const up = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  };

  // Loaded rows only: the same rows the badges beside them are drawn from, so
  // the button is live exactly when the agent can see something to clear.
  const hasUnread = rows.some((row) => (row.unreadCount ?? 0) > 0);

  const displayRows = controls.unreadFirst
    ? [...rows].sort((a, b) => Number((b.unreadCount ?? 0) > 0) - Number((a.unreadCount ?? 0) > 0))
    : rows;

  // `stale` means these rows answer the PREVIOUS filter — showing them would
  // claim the switch had not happened yet.
  const showSkeleton = stale || (loading && displayRows.length === 0);
  const moreSentinel = useInfiniteScroll({
    hasMore,
    busy: loadingMore,
    onLoadMore,
  });

  // `refreshing` outlives `loading` by the minimum-visible floor, so the union
  // is what the spinner and the bar must both follow — otherwise the icon stops
  // mid-bar on a fast response and the click looks half-registered.
  const busy = loading || refreshing;

  return (
    <div className={`cv-list${mail ? ' mail' : ''}`} style={{ width, flexBasis: width }}>
      <div className="cv-list-resize" onMouseDown={startResize} title="Drag to resize">
        <div className="cv-list-resize-line" />
      </div>
      <div className="cv-list-head">
        <div className="cv-search">
          <Search size={14} />
          <input
            value={controls.q}
            onChange={(e) => controls.setQ(e.target.value)}
            placeholder="Search people, subject, number…"
          />
        </div>
        <div className="cv-filterbar">
          <button
            type="button"
            className={`cv-filter${controls.unreadFirst ? ' on' : ''}`}
            onClick={() => controls.setUnreadFirst(!controls.unreadFirst)}
            title="Unread first"
          >
            <ListFilter size={12} />
            <span className="cv-filter-label">Unread first</span>
          </button>
          <div className="cv-filter-wrap">
            <button
              type="button"
              className="cv-filter"
              onClick={() => setOpenMenu(openMenu === 'sort' ? null : 'sort')}
              title={SORT_LABELS[controls.sortBy]}
            >
              <Clock size={12} />
              <span className="cv-filter-label">{SORT_LABELS[controls.sortBy]}</span>
              <ChevronDown size={11} />
            </button>
            {openMenu === 'sort' && (
              <div className="thr-menu" ref={menuRef} style={{ top: 30, left: 0, right: 'auto' }}>
                {(Object.keys(SORT_LABELS) as ListControls['sortBy'][])
                  .filter((key) => !VIEW_SORTS.includes(key) || controls.sortBy === key)
                  .map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      controls.setSortBy(key);
                      setOpenMenu(null);
                    }}
                  >
                    {controls.sortBy === key ? <Check size={13} /> : <span style={{ width: 13 }} />}
                    {SORT_LABELS[key]}
                  </button>
                  ))}
              </div>
            )}
          </div>
          <div className="cv-filter-wrap">
            <button
              type="button"
              className="cv-filter"
              onClick={() => setOpenMenu(openMenu === 'agent' ? null : 'agent')}
              title={AGENT_LABELS[controls.assigneeType]}
            >
              <UserRound size={12} />
              <span className="cv-filter-label">{AGENT_LABELS[controls.assigneeType]}</span>
              <ChevronDown size={11} />
            </button>
            {openMenu === 'agent' && (
              <div className="thr-menu" ref={menuRef} style={{ top: 30, left: 0, right: 'auto' }}>
                {(Object.keys(AGENT_LABELS) as ListControls['assigneeType'][]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      controls.setAssigneeType(key);
                      setOpenMenu(null);
                    }}
                  >
                    {controls.assigneeType === key ? (
                      <Check size={13} />
                    ) : (
                      <span style={{ width: 13 }} />
                    )}
                    {AGENT_LABELS[key]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              className="cv-filterbar-refresh"
              onClick={() => setOpenMenu(openMenu === 'markseen' ? null : 'markseen')}
              disabled={controls.markingSeen || !hasUnread}
              title={hasUnread ? 'Mark all read' : 'Nothing unread here'}
              aria-label="Mark all conversations read"
            >
              <CheckCheck size={15} />
            </button>
            {/* An in-UI confirm rather than window.confirm: this module also runs
                inside the dashboard's iframe, where a sandbox without
                allow-modals answers false and the click would do nothing. */}
            {openMenu === 'markseen' && (
              <div className="thr-menu cv-markall" ref={menuRef}>
                <p>
                  Mark every conversation in <strong>{controls.markAllScope}</strong> as read? This
                  clears the badge for everyone on the team.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setOpenMenu(null);
                    controls.markAllSeen();
                  }}
                >
                  <CheckCheck size={13} />
                  Mark all read
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {refreshing ? <div className="comms-progress" /> : <div className="comms-progress-slot" />}
      {controls.searchAll && (
        <div className="cv-scopebar">
          <Search size={12} />
          <span>Searching every conversation, not just {controls.scopeLabel}.</span>
          <button type="button" onClick={() => controls.setSearchAll(false)} title="Back to the view">
            <X size={12} />
          </button>
        </div>
      )}
      <div className="cv-rows" aria-busy={busy}>
        {/* Above the conversation rows and outside the skeleton: these are
            already loaded and live in this browser, so blanking them while the
            server answers for the other half would make a draft the agent just
            saved flicker out of the list they saved it into. */}
        {!showSkeleton &&
          composeDrafts.map((draft) => (
            <ComposeDraftRow
              key={`compose-${draft.id}`}
              draft={draft}
              inbox={inboxById.get(draft.inboxId)}
              onOpen={() => onOpenComposeDraft?.(draft)}
            />
          ))}
        {showSkeleton ? (
          <ConversationRowsSkeleton />
        ) : (
          displayRows.map((conversation) => {
            const named = subjectReservations.get(conversation.id) ?? null;
            const reservation = named?.booking ?? lastReservations.get(conversation.contact.id);
            return (
              <Row
                key={conversation.id}
                conversation={conversation}
                inbox={inboxById.get(conversation.inboxId)}
                agents={agents}
                active={conversation.id === selectedId}
                draft={drafts.get(conversation.id) ?? null}
                reservation={reservation}
                named={named}
                onOpenReservation={reservation ? pressJob(jobTargets(reservation.kind, reservation.id)) : null}
                onSelect={() => onSelect(conversation)}
              />
            );
          })
        )}
        {!showSkeleton && error && (
          <div className="cv-rows-error">
            <TriangleAlert size={15} />
            <span>{error}</span>
            <button type="button" className="cbtn cbtn-sm" onClick={controls.reload}>
              Try again
            </button>
          </div>
        )}
        {!showSkeleton && !error && displayRows.length === 0 && composeDrafts.length === 0 && (
          <div className="cv-rows-empty">
            {controls.q ? (
              <>
                No matches for “{controls.q}”
                {controls.searchAll ? ' in any conversation.' : ` in ${controls.scopeLabel}.`}
                {/* The view chips are triage queues; a search confined to one is
                    the usual reason a contact who is plainly in the inbox cannot
                    be found. Offer the wider search rather than silently taking it. */}
                {!controls.searchAll && (
                  <>
                    <br />
                    <button
                      type="button"
                      className="cbtn cbtn-sm"
                      style={{ marginTop: 10 }}
                      onClick={() => controls.setSearchAll(true)}
                    >
                      Search all conversations
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                No conversations here yet.
                <br />
                New inbound messages appear the moment they arrive.
              </>
            )}
          </div>
        )}
        {!showSkeleton && hasMore && (
          <>
            {/* Reaching the end of the rows IS the request for more of them —
                the button stays for the times the observer cannot fire. */}
            <div ref={moreSentinel} aria-hidden />
            <button
              type="button"
              className="cv-rows-more"
              onClick={onLoadMore}
              disabled={loadingMore}
            >
              {loadingMore && <span className="btn-spin" />}
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
